import {
    Box, Button, Card, CardContent, Chip,
    Stack, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import {useState} from 'react';
import {iapApi} from '../../api/iapApi';
import type {IapProduct, IapProductCreateReq, IapProductUpdateReq} from '../../types/iap';
import {StoreSyncStatusChip} from './StoreSyncStatusChip';
import {IapProductFormDialog} from './IapProductFormDialog';

interface Props {
    products: IapProduct[];
    onRefresh: () => void;
}

type TabValue = 'all' | 'GOOGLE' | 'APPLE';

export function IapProductList({products, onRefresh}: Props) {
    const [tab,        setTab]        = useState<TabValue>('all');
    const [formOpen,   setFormOpen]   = useState(false);
    const [editTarget, setEditTarget] = useState<IapProduct | undefined>();
    const [syncing,    setSyncing]    = useState(false);

    const filtered    = tab === 'all' ? products : products.filter(p => p.store === tab);
    const googleCount = products.filter(p => p.store === 'GOOGLE').length;
    const appleCount  = products.filter(p => p.store === 'APPLE').length;
    const showStore   = tab === 'all';
    const showBase    = tab === 'GOOGLE';

    const handleCreate = async (data: IapProductCreateReq | IapProductUpdateReq) => {
        try {
            await iapApi.create(data as IapProductCreateReq);
            onRefresh();
        } catch (e) {
            alert(`상품 등록 실패: ${e instanceof Error ? e.message : String(e)}`);
            throw e;
        }
    };

    const handleUpdate = async (data: IapProductCreateReq | IapProductUpdateReq) => {
        if (!editTarget) return;
        try {
            await iapApi.update(editTarget.id, data as IapProductUpdateReq);
            onRefresh();
        } catch (e) {
            alert(`상품 수정 실패: ${e instanceof Error ? e.message : String(e)}`);
            throw e;
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('삭제하시겠습니까?')) return;
        try {
            await iapApi.delete(id);
            onRefresh();
        } catch (e) {
            alert(`상품 삭제 실패: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const handleSyncFromStores = async () => {
        if (!confirm('스토어에서 상품 정보를 가져와 DB와 결제 테이블을 동기화합니다.\n계속하시겠습니까?')) return;
        setSyncing(true);
        try {
            const res = await iapApi.syncFromStores();
            const msgs = [`생성: ${res.created}개, 업데이트: ${res.updated}개`];
            if (res.errors.length > 0) msgs.push(`오류:\n${res.errors.join('\n')}`);
            alert(msgs.join('\n'));
            onRefresh();
        } finally {
            setSyncing(false);
        }
    };

    const productTypeLabel = (p: IapProduct) =>
        p.productType === 'SUBSCRIPTION'
            ? `구독 · ${p.billingPeriod ?? ''}`
            : `포인트 · ${p.pointAmount ?? 0}P`;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">IAP 상품 관리</Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleSyncFromStores} disabled={syncing}>
                        {syncing ? '동기화 중...' : '스토어에서 동기화'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => { setEditTarget(undefined); setFormOpen(true); }}
                    >
                        + 상품 등록
                    </Button>
                </Stack>
            </Stack>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{mb: 2}}>
                <Tab label={`전체 (${products.length})`} value="all"    />
                <Tab label={`Google Play (${googleCount})`} value="GOOGLE" />
                <Tab label={`App Store (${appleCount})`}   value="APPLE"  />
            </Tabs>

            <Card>
                <CardContent sx={{p: 0}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>내부 키 / 타입</TableCell>
                                <TableCell>상품명</TableCell>
                                <TableCell>가격</TableCell>
                                {showStore && <TableCell>스토어</TableCell>}
                                <TableCell>Product ID</TableCell>
                                {showBase  && <TableCell>Base Plan ID</TableCell>}
                                <TableCell>동기화</TableCell>
                                <TableCell>활성</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{p.internalKey}</Typography>
                                        <Chip size="small" label={productTypeLabel(p)} variant="outlined" />
                                    </TableCell>
                                    <TableCell>{p.displayName}</TableCell>
                                    <TableCell>₩{p.priceKrw.toLocaleString()}</TableCell>
                                    {showStore && (
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={p.store === 'GOOGLE' ? 'Google' : 'Apple'}
                                                color={p.store === 'GOOGLE' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Typography variant="caption" sx={{fontFamily: 'monospace'}}>
                                            {p.productId ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    {showBase && (
                                        <TableCell>
                                            <Typography variant="caption" sx={{fontFamily: 'monospace'}}>
                                                {p.basePlanId ?? '-'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <StoreSyncStatusChip status={p.syncStatus} error={p.syncError} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={p.isActive ? 'success' : 'default'}
                                            label={p.isActive ? '활성' : '비활성'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <Button
                                                size="small"
                                                onClick={() => { setEditTarget(p); setFormOpen(true); }}
                                            >
                                                수정
                                            </Button>
                                            <Button size="small" color="error" onClick={() => handleDelete(p.id)}>
                                                삭제
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <IapProductFormDialog
                key={editTarget?.id ?? 'create'}
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={editTarget ? handleUpdate : handleCreate}
                initial={editTarget}
            />
        </Box>
    );
}
